import type { BoxPoint, BoxSelector, MaidrLayer } from '@type/grammar';
import type { Movable } from '@type/movable';
import type { AudioState, BrailleState, DescriptionState, TextState } from '@type/state';
import type { Dimension, NearestPoint } from './abstract';
import { BoxplotSection } from '@type/boxplotSection';
import { Orientation } from '@type/grammar';
import { Constant } from '@util/constant';
import { MathUtil } from '@util/math';
import { Svg } from '@util/svg';
import { AbstractTrace } from './abstract';
import { MovableGrid } from './movable';

/**
 * Concrete implementation of a box plot trace supporting vertical and horizontal orientations.
 * Handles boxplot sections (min, Q1, Q2, Q3, max, outliers) and rotor-based navigation.
 *
 * This is a pure box plot — violin-specific behavior lives in ViolinBoxTrace.
 */
export class BoxTrace extends AbstractTrace {
  protected readonly supportsExtrema = false;
  protected readonly movable: Movable;

  private readonly points: BoxPoint[];
  private readonly boxValues: (number[] | number)[][];
  protected readonly highlightValues: (SVGElement[] | SVGElement)[][] | null;
  protected highlightCenters:
    | { x: number; y: number; row: number; col: number; element: SVGElement }[]
    | null;

  private readonly orientation: Orientation;
  private readonly sections: string[];

  private readonly min: number;
  private readonly max: number;

  /**
   * Compute box values array based on section accessors and orientation.
   * Handles the transformation from section-based to position-based layout.
   *
   * @param sectionAccessors - Array of functions that extract section values from BoxPoint
   * @returns 2D array where layout depends on orientation:
   *         - Vertical: [sections][positions]
   *         - Horizontal: [positions][sections]
   */
  private computeBoxValues(sectionAccessors: ((p: BoxPoint) => number | number[])[]): (number[] | number)[][] {
    if (this.orientation === Orientation.HORIZONTAL) {
      return this.points.map(point =>
        sectionAccessors.map(accessor => accessor(point)),
      );
    } else {
      return sectionAccessors.map(accessor =>
        this.points.map(point => accessor(point)),
      );
    }
  }

  constructor(layer: MaidrLayer) {
    super(layer);

    this.orientation = layer.orientation ?? Orientation.VERTICAL;

    // For horizontal orientation, reverse points to match visual order (lower-left start)
    if (this.orientation === Orientation.HORIZONTAL) {
      this.points = [...(layer.data as BoxPoint[])].reverse();
    } else {
      this.points = layer.data as BoxPoint[];
    }

    // Standard box plot sections: full Tukey structure
    const { sections, accessors } = this.buildStandardBoxDataSections();
    this.sections = sections;
    this.boxValues = this.computeBoxValues(accessors);

    const flatBoxValues = this.boxValues.map(row =>
      row.flatMap(cell => (Array.isArray(cell) ? cell : [cell])),
    );
    const filteredValues = flatBoxValues.map(row =>
      row.filter(value => !Number.isNaN(value)),
    );
    this.min = MathUtil.minFrom2D(filteredValues);
    this.max = MathUtil.maxFrom2D(filteredValues);

    this.highlightValues = this.mapToSvgElements(
      layer.selectors as BoxSelector[],
    );

    if (this.orientation === Orientation.HORIZONTAL) {
      this.highlightValues?.reverse();
    }

    this.highlightCenters = this.mapSvgElementsToCenters();
    this.movable = new MovableGrid<number[] | number>(this.boxValues, { row: 0 });
  }

  /**
   * Build data sections and accessors for standard box plots.
   * Exposes full Tukey structure including all quartiles.
   */
  private buildStandardBoxDataSections(): {
    sections: string[];
    accessors: ((p: BoxPoint) => number | number[])[];
  } {
    return {
      sections: [
        BoxplotSection.LOWER_OUTLIER,
        BoxplotSection.MIN,
        BoxplotSection.Q1,
        BoxplotSection.Q2,
        BoxplotSection.Q3,
        BoxplotSection.MAX,
        BoxplotSection.UPPER_OUTLIER,
      ],
      accessors: [
        (p: BoxPoint) => p.lowerOutliers,
        (p: BoxPoint) => p.min,
        (p: BoxPoint) => p.q1,
        (p: BoxPoint) => p.q2,
        (p: BoxPoint) => p.q3,
        (p: BoxPoint) => p.max,
        (p: BoxPoint) => p.upperOutliers,
      ],
    };
  }

  /**
   * Gets the description state for the box plot trace.
   * @returns The description state containing chart metadata and data table
   */
  public get description(): DescriptionState {
    const groupNames = this.points.map(p => p.z).join(', ');

    const stats: DescriptionState['stats'] = [
      { label: 'Number of groups', value: this.points.length },
      { label: 'Group names', value: groupNames },
      { label: 'Min', value: this.min },
      { label: 'Max', value: this.max },
    ];

    const headers = ['Group', 'Min', 'Q1', 'Q2', 'Q3', 'Max'];
    const rows: (string | number)[][] = this.points.map(p => [
      p.z,
      p.min,
      p.q1,
      p.q2,
      p.q3,
      p.max,
    ]);

    return {
      chartType: this.getChartTypeLabel(),
      title: this.title,
      axes: this.getDescriptionAxes(),
      stats,
      dataTable: { headers, rows },
    };
  }

  public override dispose(): void {
    this.points.length = 0;
    this.sections.length = 0;
    super.dispose();
  }

  public override moveToIndex(row: number, col: number): boolean {
    return super.moveToIndex(row, col);
  }

  protected get values(): (number[] | number)[][] {
    return this.boxValues;
  }

  protected get audio(): AudioState {
    const isHorizontal = this.orientation === Orientation.HORIZONTAL;
    const value = this.boxValues[this.row][this.col];
    const index = isHorizontal ? this.col : this.row;

    const panning = Array.isArray(value)
      ? value.length === 0 ? index : value[value.length - 1] - this.min
      : Number.isNaN(value) ? index : value - this.min;
    return {
      freq: {
        min: this.min,
        max: this.max,
        raw: this.boxValues[this.row][this.col],
      },
      panning: {
        x: isHorizontal ? panning : this.row,
        y: isHorizontal ? this.row : panning,
        rows: isHorizontal ? this.boxValues.length : this.max - this.min,
        cols: isHorizontal ? this.max - this.min : this.boxValues.length,
      },
    };
  }

  protected get braille(): BrailleState {
    const isHorizontal = this.orientation === Orientation.HORIZONTAL;
    const row = isHorizontal ? this.row : this.col;
    const col = isHorizontal ? this.col : this.row;

    return {
      empty: false,
      id: this.id,
      values: this.points,
      min: this.min,
      max: this.max,
      row,
      col,
    };
  }

  protected get text(): TextState {
    const isHorizontal = this.orientation === Orientation.HORIZONTAL;
    const point = isHorizontal ? this.points[this.row] : this.points[this.col];

    const mainLabel = isHorizontal ? this.yAxis : this.xAxis;
    const section = isHorizontal
      ? this.sections[this.col]
      : this.sections[this.row];

    const crossLabel = isHorizontal ? this.xAxis : this.yAxis;
    const crossValue = this.boxValues[this.row][this.col];

    return {
      main: { label: mainLabel, value: point.z },
      cross: { label: crossLabel, value: crossValue },
      section,
      mainAxis: isHorizontal ? 'y' : 'x',
      crossAxis: isHorizontal ? 'x' : 'y',
    };
  }

  protected get dimension(): Dimension {
    // boxValues is orientation-normalized (row = navigation row, col =
    // navigation col), so rows/cols map directly to its shape.
    return {
      rows: this.boxValues.length,
      cols: this.boxValues[this.row].length,
    };
  }

  private mapToSvgElements(
    selectors: BoxSelector[] | undefined,
  ): (SVGElement[] | SVGElement)[][] | null {
    if (!selectors || selectors.length !== this.points.length) {
      return null;
    }

    const isVertical = this.orientation === Orientation.VERTICAL;
    const svgElements = new Array<Array<SVGElement[] | SVGElement>>();

    if (isVertical) {
      for (let i = 0; i < this.sections.length; i++) {
        svgElements.push(Array.from({ length: selectors.length }));
      }
    }

    // Phase 1: Collect all original elements without cloning (prevents nth-child() DOM shifts)
    const originals: Array<{
      lowerOutliers: SVGElement[];
      upperOutliers: SVGElement[];
      min: SVGElement | null;
      max: SVGElement | null;
      iq: SVGElement | null;
      q2: SVGElement | null;
      q1Direct: SVGElement | null;
      q3Direct: SVGElement | null;
    }> = [];

    selectors.forEach((selector) => {
      const lowerOutliersOriginals = selector.lowerOutliers?.flatMap(s =>
        Svg.selectAllElements(s, false),
      ) ?? [];
      const upperOutliersOriginals = selector.upperOutliers?.flatMap(s =>
        Svg.selectAllElements(s, false),
      ) ?? [];

      const minOriginal = Svg.selectElement(selector.min, false);
      const maxOriginal = Svg.selectElement(selector.max, false);
      const iqOriginal = Svg.selectElement(selector.iq, false);
      const q2Original = Svg.selectElement(selector.q2, false);

      // Direct Q1/Q3 selectors bypass iq edge derivation (used by Plotly)
      const q1DirectOriginal = selector.q1 ? Svg.selectElement(selector.q1, false) : null;
      const q3DirectOriginal = selector.q3 ? Svg.selectElement(selector.q3, false) : null;

      originals.push({
        lowerOutliers: lowerOutliersOriginals,
        upperOutliers: upperOutliersOriginals,
        min: minOriginal,
        max: maxOriginal,
        iq: iqOriginal,
        q2: q2Original,
        q1Direct: q1DirectOriginal,
        q3Direct: q3DirectOriginal,
      });
    });

    // Phase 2: Clone and create elements from originals (DOM queries complete)
    originals.forEach((original, boxIdx) => {
      const lowerOutliers = original.lowerOutliers.map((el) => {
        const clone = Svg.markOwned(el.cloneNode(true) as SVGElement);
        clone.setAttribute(Constant.VISIBILITY, Constant.HIDDEN);
        el.insertAdjacentElement(Constant.AFTER_END, clone);
        return clone;
      });
      const upperOutliers = original.upperOutliers.map((el) => {
        const clone = Svg.markOwned(el.cloneNode(true) as SVGElement);
        clone.setAttribute(Constant.VISIBILITY, Constant.HIDDEN);
        el.insertAdjacentElement(Constant.AFTER_END, clone);
        return clone;
      });

      const min = this.cloneElementOrEmpty(original.min);
      const max = this.cloneElementOrEmpty(original.max);
      const q2 = this.cloneElementOrEmpty(original.q2);

      // Use direct Q1/Q3 selectors if provided (Plotly: highlight entire box).
      // Otherwise, derive Q1/Q3 line elements from iq edges (matplotlib/seaborn).
      let q1: SVGElement;
      let q3: SVGElement;
      if (original.q1Direct && original.q3Direct) {
        q1 = this.cloneElementOrEmpty(original.q1Direct);
        q3 = this.cloneElementOrEmpty(original.q3Direct);
      } else {
        const isIqrReversed = this.layer.domMapping?.iqrDirection === 'reverse';
        [q1, q3] = original.iq
          ? (isVertical
              ? isIqrReversed
                ? [
                    Svg.createLineElement(original.iq, 'top'),
                    Svg.createLineElement(original.iq, 'bottom'),
                  ]
                : [
                    Svg.createLineElement(original.iq, 'bottom'),
                    Svg.createLineElement(original.iq, 'top'),
                  ]
              : [
                  Svg.createLineElement(original.iq, 'left'),
                  Svg.createLineElement(original.iq, 'right'),
                ])
          : [
              Svg.createEmptyElement('line'),
              Svg.createEmptyElement('line'),
            ];
      }

      const sections = [lowerOutliers, min, q1, q2, q3, max, upperOutliers];

      if (isVertical) {
        sections.forEach((section, sectionIdx) => {
          svgElements[sectionIdx][boxIdx] = section;
        });
      } else {
        svgElements.push(sections);
      }
    });

    return svgElements;
  }

  /**
   * Clones an SVG element with hidden visibility or returns an empty element.
   */
  private cloneElementOrEmpty(original: SVGElement | null): SVGElement {
    if (!original) {
      return Svg.createEmptyElement();
    }
    const clone = Svg.markOwned(original.cloneNode(true) as SVGElement);
    clone.setAttribute(Constant.VISIBILITY, Constant.HIDDEN);
    original.insertAdjacentElement(Constant.AFTER_END, clone);
    return clone;
  }

  /**
   * Moves to the next boxplot section that matches the comparison criteria.
   */
  public override moveToNextCompareValue(direction: 'left' | 'right' | 'up' | 'down', type: 'lower' | 'higher'): boolean {
    const currentGroup = this.row;
    if (currentGroup < 0 || currentGroup >= this.boxValues.length) {
      return false;
    }
    let values: any[] = [];
    let currentIndex = 0;

    if (direction === 'left' || direction === 'right') {
      values = this.boxValues[this.row];
      currentIndex = this.col;
    } else {
      values = this.boxValues.map(box => box[this.col]);
      currentIndex = this.row;
    }
    if (values.length <= 0) {
      return false;
    }

    const step = direction === 'right' || direction === 'up' ? 1 : -1;
    let i = currentIndex + step;

    while (i >= 0 && i < values.length) {
      const current_value = values[currentIndex];
      const next_value = values[i];
      if (Array.isArray(next_value) || Array.isArray(current_value)) {
        // Outlier sections hold arrays, not scalar section values; skip them
        // instead of falsely reporting a successful move.
        i += step;
        continue;
      }

      if (this.compare(next_value, current_value, type)) {
        this.set_point(direction, i);
        this.updateVisualPointPosition();
        this.notifyStateUpdate();
        return true;
      }
      i += step;
    }
    this.notifyRotorBounds();
    return false;
  }

  /**
   * Sets the current point based on direction and index.
   */
  public set_point(direction: 'left' | 'right' | 'up' | 'down', pointIndex: number): void {
    if (direction === 'left' || direction === 'right') {
      this.col = pointIndex;
    } else {
      this.row = pointIndex;
    }
  }

  public override moveUpRotor(mode: 'lower' | 'higher'): boolean {
    if (this.orientation === Orientation.VERTICAL) {
      this.moveOnce('UPWARD');
      return true;
    }
    return this.moveToNextCompareValue('up', mode);
  }

  public override moveDownRotor(mode: 'lower' | 'higher'): boolean {
    if (this.orientation === Orientation.VERTICAL) {
      this.moveOnce('DOWNWARD');
      return true;
    }
    return this.moveToNextCompareValue('down', mode);
  }

  public override moveLeftRotor(mode: 'lower' | 'higher'): boolean {
    if (this.orientation === Orientation.HORIZONTAL) {
      this.moveOnce('BACKWARD');
      return true;
    }
    return this.moveToNextCompareValue('left', mode);
  }

  public override moveRightRotor(mode: 'lower' | 'higher'): boolean {
    if (this.orientation === Orientation.HORIZONTAL) {
      this.moveOnce('FORWARD');
      return true;
    }
    return this.moveToNextCompareValue('right', mode);
  }

  protected mapSvgElementsToCenters():
    | { x: number; y: number; row: number; col: number; element: SVGElement }[]
    | null {
    const svgElements: (SVGElement | SVGElement[])[][] | null = this.highlightValues;

    if (!svgElements) {
      return null;
    }

    const centers: {
      x: number;
      y: number;
      row: number;
      col: number;
      element: SVGElement;
    }[] = [];
    for (let row = 0; row < svgElements.length; row++) {
      for (let col = 0; col < svgElements[row].length; col++) {
        const element = svgElements[row][col];
        const targetElement = Array.isArray(element) ? element[0] : element;
        if (targetElement) {
          const bbox = targetElement.getBoundingClientRect();
          centers.push({
            x: bbox.x + bbox.width / 2,
            y: bbox.y + bbox.height / 2,
            row,
            col,
            element: targetElement,
          });
        }
      }
    }

    return centers;
  }

  public findNearestPoint(
    x: number,
    y: number,
  ): NearestPoint | null {
    if (!this.highlightCenters) {
      return null;
    }

    let nearestDistance = Infinity;
    let nearestIndex = -1;

    for (let i = 0; i < this.highlightCenters.length; i++) {
      const center = this.highlightCenters[i];
      const distance = Math.hypot(center.x - x, center.y - y);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = i;
      }
    }

    if (nearestIndex === -1) {
      return null;
    }

    return {
      element: this.highlightCenters[nearestIndex].element,
      row: this.highlightCenters[nearestIndex].row,
      col: this.highlightCenters[nearestIndex].col,
      centerX: this.highlightCenters[nearestIndex].x,
      centerY: this.highlightCenters[nearestIndex].y,
    };
  }

  /**
   * Hover-driven movement is disabled for boxplots, but pointer guidance
   * still surfaces directional cues toward the nearest box element.
   *
   * Parameters retained on the signature (rather than the zero-arg form
   * TypeScript permits) so the override matches the base contract at a
   * glance.
   */
  protected override moveToNearest(
    _x: number,
    _y: number,
    _nearest: NearestPoint,
    _onCurve: boolean,
  ): void {
    // Disabled for boxplots
  }
}
